// src/hooks/useBiodataManagement.js
import { useState, useCallback, useMemo } from "react";
import { useDataFetching } from "./useDataFetching";
import { useBulkActions } from "./useBulkActions";
import { useModalState } from "./useModalState";
import { useNotifications } from "./useNotifications";
import { useFiltersWithPagination } from "./usePagination";
import { exportBiodataToExcel, showExportNotification, validateExportData } from "../utils/excelExport";
// import { api } from '../utils/api';

/**
 * Comprehensive hook for biodata management across different pages
 * @param {Object} config - Configuration object
 * @returns {Object} Complete biodata management state and handlers
 */
export const useBiodataManagement = (config = {}) => {
  const {
    apiEndpoints = {},
    initialFilters = {},
    pageSize = 10,
    enableBulkActions = true,
    enableModals = true,
    enableNotifications = true,
    biodataType = "general", // 'pending', 'rejected', 'approved', 'amended'
  } = config;

  // Core state
  const [analytics, setAnalytics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    amended: 0,
    totalRejected: 0,
    recentRejections: 0,
    editPermissionsGranted: 0,
    topRejectionReasons: [],
    rejectionTrends: [],
    recruiterStats: [],
  });

  // Add state for selectedEditData
  const [selectedEditData, setSelectedEditData] = useState(null);

  const initialExportStatus = useMemo(
    () => ({
      isExporting: false,
      stage: "idle",
      progress: null,
      totalRecords: 0,
      estimatedSeconds: 0,
      downloadedBytes: 0,
      filename: "",
      scope: "all",
      error: null,
    }),
    []
  );

  const [exportStatus, setExportStatus] = useState(initialExportStatus);

  // Filters and pagination
  const {
    filters,
    updateFilter,
    resetFilters,
    page,
    pageSize: currentPageSize,
    goToPage,
    changePageSize,
    resetPagination,
  } = useFiltersWithPagination(initialFilters, pageSize);

  // Data fetching
  const fetchBiodata = useCallback(
    async (params = {}) => {
      const { signal, ...otherParams } = params;
      const token = localStorage.getItem("authToken");
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

      try {
        const queryParams = new URLSearchParams({
          page: page,
          limit: currentPageSize,
          ...filters,
          ...otherParams,
        });

        for (const [key, value] of queryParams.entries()) {
          if (value === "" || value === null || value === undefined) {
            queryParams.delete(key);
          }
        }

        const response = await fetch(
          `${API_BASE_URL}${apiEndpoints.list}?${queryParams.toString()}`,
          {
            signal,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("user");
            window.location.href = "/login-screen";
          }
          throw new Error("Network response was not ok");
        }

        const result = await response.json();

        // Handle different response formats from Laravel API
        let data;
        if (result.status === "success" && result.data) {
          // Laravel standard response format
          data = result.data;
        } else if (result.data) {
          // Direct data response
          data = result;
        } else {
          // Fallback to result itself
          data = result;
        }

        // Update analytics if present
        if (data.analytics || result.analytics) {
          setAnalytics(data.analytics || result.analytics);
        }

        // Extract biodata list from various possible response structures
        let biodataList = [];
        if (Array.isArray(data)) {
          biodataList = data;
        } else if (data.biodata && Array.isArray(data.biodata)) {
          biodataList = data.biodata;
        } else if (data.data && Array.isArray(data.data)) {
          biodataList = data.data;
        } else if (data.items && Array.isArray(data.items)) {
          biodataList = data.items;
        }

        // Extract pagination info from API response
        // Backend returns: { current_page, per_page, total, total_pages, from, to }
        const paginationData = data.pagination || {};
        const totalItems =
          paginationData.total ||
          data.total ||
          data.totalCount ||
          data.total_count ||
          biodataList.length ||
          0;
        const totalPages =
          paginationData.total_pages ||
          paginationData.totalPages ||
          data.totalPages ||
          data.total_pages ||
          data.last_page ||
          Math.ceil(totalItems / currentPageSize);
        const currentPage =
          paginationData.current_page ||
          paginationData.currentPage ||
          data.currentPage ||
          data.current_page ||
          page;

        return {
          items: biodataList,
          totalItems,
          totalPages,
          currentPage,
          analytics: data.analytics || result.analytics,
        };
      } catch (error) {
        console.error("Error fetching biodata:", error);
        throw error;
      }
    },
    [apiEndpoints.list, page, currentPageSize, filters]
  );

  const {
    data: biodataData,
    loading,
    error,
    refetch,
    reset: resetData,
  } = useDataFetching(fetchBiodata, {
    dependencies: [page, currentPageSize, filters],
  });

  // Extract biodata list and pagination info
  const biodata = biodataData?.items || [];
  const totalItems = biodataData?.totalItems || 0;
  const totalPages = biodataData?.totalPages || 0;

  // Bulk actions
  const bulkActions = useBulkActions();

  // Modal management
  const modals = useModalState();

  // Notifications
  const notifications = useNotifications();

  // API action handlers
  const approveBiodata = useCallback(
    async (biodataId, reason = "") => {
      const token = localStorage.getItem("authToken");
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";
      try {
        // Replace {id} placeholder with actual biodataId
        const endpoint = apiEndpoints.approve.replace("{id}", biodataId);

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason }),
        });

        const result = await response.json();

        if (response.ok) {
          await refetch();
          // Handle Laravel response format
          const message =
            result.message ||
            result.data?.message ||
            "Biodata approved successfully";
          return { success: true, message };
        } else {
          const message =
            result.message || result.error || "Failed to approve biodata";
          return { success: false, message };
        }
      } catch (error) {
        console.error("Error approving biodata:", error);
        return {
          success: false,
          message: "An error occurred while approving biodata",
        };
      }
    },
    [apiEndpoints.approve, refetch]
  );

  const rejectBiodata = useCallback(
    async (biodataId, reason = "") => {
      const token = localStorage.getItem("authToken");
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";
      try {
        // Replace {id} placeholder with actual biodataId
        const endpoint = apiEndpoints.reject.replace("{id}", biodataId);

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason }),
        });

        const result = await response.json();

        if (response.ok) {
          await refetch();
          // Handle Laravel response format
          const message =
            result.message ||
            result.data?.message ||
            "Biodata rejected successfully";
          return { success: true, message };
        } else {
          const message =
            result.message || result.error || "Failed to reject biodata";
          return { success: false, message };
        }
      } catch (error) {
        console.error("Error rejecting biodata:", error);
        return {
          success: false,
          message: "An error occurred while rejecting biodata",
        };
      }
    },
    [apiEndpoints.reject, refetch]
  );

  const grantEditPermission = useCallback(
    async (biodataId, reason, expires_at) => {
      const token = localStorage.getItem("authToken");
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";
      try {
        // Replace {id} placeholder with actual biodataId
        const endpoint = apiEndpoints.grantPermission.replace(
          "{id}",
          biodataId
        );
        const body = { reason };
        if (expires_at) body.expires_at = expires_at;

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        const result = await response.json();

        if (response.ok) {
          await refetch();
          // Handle Laravel response format
          const message =
            result.message ||
            result.data?.message ||
            "Edit permission granted successfully";
          return { success: true, message };
        } else {
          const message =
            result.message || result.error || "Failed to grant edit permission";
          return { success: false, message };
        }
      } catch (error) {
        console.error("Error granting edit permission:", error);
        return {
          success: false,
          message: "An error occurred while granting edit permission",
        };
      }
    },
    [apiEndpoints.grantPermission, refetch]
  );

  // Bulk action handlers
  const handleBulkApprove = useCallback(
    async (selectedIds) => {
      return bulkActions.bulkApprove(async (ids) => {
        const results = await Promise.allSettled(
          ids.map((id) => approveBiodata(id))
        );

        const successful = results.filter(
          (r) => r.status === "fulfilled" && r.value.success
        ).length;
        const failed = results.length - successful;

        if (failed === 0) {
          return {
            success: true,
            message: `${successful} biodata approved successfully`,
          };
        } else {
          return {
            success: false,
            message: `${successful} approved, ${failed} failed`,
          };
        }
      });
    },
    [bulkActions, approveBiodata]
  );

  const handleBulkReject = useCallback(
    async (reason = "") => {
      return bulkActions.bulkReject(async (ids) => {
        const results = await Promise.allSettled(
          ids.map((id) => rejectBiodata(id, reason))
        );

        const successful = results.filter(
          (r) => r.status === "fulfilled" && r.value.success
        ).length;
        const failed = results.length - successful;

        if (failed === 0) {
          return {
            success: true,
            message: `${successful} biodata rejected successfully`,
          };
        } else {
          return {
            success: false,
            message: `${successful} rejected, ${failed} failed`,
          };
        }
      });
    },
    [bulkActions, rejectBiodata]
  );

  const handleBulkGrantPermission = useCallback(async () => {
    return bulkActions.bulkGrantPermission(async (ids) => {
      const results = await Promise.allSettled(
        ids.map((id) => grantEditPermission(id))
      );

      const successful = results.filter(
        (r) => r.status === "fulfilled" && r.value.success
      ).length;
      const failed = results.length - successful;

      if (failed === 0) {
        return {
          success: true,
          message: `Edit permission granted to ${successful} biodata successfully`,
        };
      } else {
        return {
          success: false,
          message: `${successful} granted, ${failed} failed`,
        };
      }
    });
  }, [bulkActions, grantEditPermission]);

  // Export/Print handlers
  const exportBiodata = useCallback(
    async (selectedIds = [], format = "excel", exportOptions = {}) => {
      const {
        statusFilter = "all",
        includeDocuments = false,
        includePhotos = false,
        useBackend = true,
        exportScope = "all",
        responseType = "stream",
        filters: filterOverrides = null,
        pagination = null,
      } = exportOptions;

      const ids = selectedIds.length > 0 ? selectedIds : biodata.map((b) => b.id);
      const totalHint =
        exportScope === "current_page"
          ? pagination?.perPage || ids.length || biodata.length || totalItems
          : totalItems || ids.length || biodata.length;
      const startTimestamp = Date.now();

      setExportStatus({
        ...initialExportStatus,
        isExporting: true,
        stage: "preparing",
        totalRecords: totalHint,
        scope: exportScope,
      });

      if (notifications.showNotification) {
        showExportNotification(notifications.showNotification, "start", {
          totalRecords: totalHint,
          scope: exportScope,
        });
      }

      try {
        let filename = `biodata_${(statusFilter || exportScope || "all")
          .toString()
          .toLowerCase()}_${new Date().toISOString().split("T")[0]}.xlsx`;

        if (useBackend && apiEndpoints.export) {
          const token = localStorage.getItem("authToken");
          const API_BASE_URL =
            import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

          const payload = {
            format,
            export_scope: exportScope,
            response_type: responseType,
            include_documents: includeDocuments,
            include_photos: includePhotos,
          };

          if (statusFilter && statusFilter !== "all") {
            payload.status = statusFilter;
          }

          const activeFilters = filterOverrides || filters || {};
          if (activeFilters.recruiter_id) {
            payload.recruiter_id = Number(activeFilters.recruiter_id);
          }
          if (activeFilters.vdr_type) {
            payload.vdr_type = activeFilters.vdr_type;
          }
          if (activeFilters.date_from) {
            payload.date_from = activeFilters.date_from;
          }
          if (activeFilters.date_to) {
            payload.date_to = activeFilters.date_to;
          }

          const shouldSendIds =
            exportScope === "current_page" &&
            (!pagination || !pagination.page || !pagination.perPage) &&
            ids.length > 0;

          if (shouldSendIds) {
            payload.ids = ids;
          } else if (exportScope !== "all" && selectedIds.length > 0) {
            payload.ids = selectedIds;
          }

          if (
            exportScope === "current_page" &&
            pagination &&
            pagination.page &&
            pagination.perPage
          ) {
            payload.page = pagination.page;
            payload.page_size = pagination.perPage;
          }

          const response = await fetch(
            `${API_BASE_URL}${apiEndpoints.export}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token || ""}`,
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
            const errorMessage = await response.text();
            throw new Error(errorMessage || "Export request failed");
          }

          const totalRecordsHeader = Number(
            response.headers.get("x-total-records") || 0
          );
          const estimatedSecondsHeader = Number(
            response.headers.get("x-estimated-seconds") || 0
          );
          const scopeHeader =
            response.headers.get("x-export-scope") || exportScope;

          setExportStatus((prev) => ({
            ...prev,
            totalRecords: totalRecordsHeader || prev.totalRecords,
            estimatedSeconds: estimatedSecondsHeader || prev.estimatedSeconds,
            scope: scopeHeader,
            stage: "downloading",
          }));

          if (notifications.showNotification) {
            showExportNotification(notifications.showNotification, "progress", {
              totalRecords: totalRecordsHeader || totalHint,
              estimatedSeconds: estimatedSecondsHeader,
              scope: scopeHeader,
            });
          }

          const contentType = response.headers.get("content-type") || "";
          const contentDisposition =
            response.headers.get("content-disposition") || "";
          const contentLength = Number(
            response.headers.get("content-length") || 0
          );

          if (contentType.includes("application/json")) {
            const result = await response.json();

            if (result.status === "success" && result.data) {
              const exportData = result.data;
              filename = result.filename || filename;

              const validation = validateExportData(exportData, {
                maxRecords: 200000,
                requiredFields: ["ID", "Full Name", "Passport No"],
              });

              if (!validation.isValid) {
                throw new Error(
                  `Data validation failed: ${validation.errors.join(", ")}`
                );
              }

              const exportResult = await exportBiodataToExcel(exportData, {
                filename,
                statusFilter: scopeHeader,
                includeDocuments,
                includePhotos,
                isPreformatted: true,
              });

              setExportStatus({
                ...initialExportStatus,
                stage: "completed",
                totalRecords: exportData.length,
                filename: exportResult.filename,
                scope: scopeHeader,
                estimatedSeconds: estimatedSecondsHeader,
              });

              if (notifications.showNotification) {
                showExportNotification(
                  notifications.showNotification,
                  "success",
                  {
                    totalRecords: exportData.length,
                    filename: exportResult.filename,
                    durationSeconds: (Date.now() - startTimestamp) / 1000,
                    scope: scopeHeader,
                  }
                );
              }

              return { success: true, ...exportResult };
            }

            throw new Error(result.message || "Export failed");
          }

          if (!response.body) {
            throw new Error("Streaming not supported in this browser");
          }

          const reader = response.body.getReader();
          const chunks = [];
          let downloadedBytes = 0;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
              chunks.push(value);
              downloadedBytes += value.length;
              setExportStatus((prev) => ({
                ...prev,
                downloadedBytes,
                progress:
                  contentLength > 0
                    ? Math.min(
                        99,
                        Math.round((downloadedBytes / contentLength) * 100)
                      )
                    : prev.progress,
                stage: "downloading",
              }));
            }
          }

          reader.releaseLock();

          const blob = new Blob(chunks, {
            type: contentType || "application/octet-stream",
          });

          const filenameMatch =
            /filename\\*?=([^;]+)/i.exec(contentDisposition) ||
            /filename="?([^\";]+)"?/i.exec(contentDisposition);
          filename =
            (filenameMatch && decodeURIComponent(filenameMatch[1].trim())) ||
            filename;

          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);

          setExportStatus((prev) => ({
            ...prev,
            isExporting: false,
            stage: "completed",
            progress: 100,
            filename,
            totalRecords:
              prev.totalRecords ||
              totalRecordsHeader ||
              totalHint ||
              0,
            downloadedBytes,
          }));

          if (notifications.showNotification) {
            showExportNotification(
              notifications.showNotification,
              "success",
              {
                totalRecords:
                  totalRecordsHeader || totalHint || prev.totalRecords || 0,
                filename,
                durationSeconds: (Date.now() - startTimestamp) / 1000,
                scope: scopeHeader,
              }
            );
          }

          return {
            success: true,
            filename,
            totalRecords: totalRecordsHeader || totalHint || 0,
          };
        }

        // Fallback to frontend processing
        console.warn(
          "Backend export not available, falling back to frontend processing"
        );

        const exportData =
          selectedIds.length > 0
            ? biodata.filter((b) => selectedIds.includes(b.id))
            : biodata;

        if (exportData.length === 0) {
          throw new Error("No biodata to export");
        }

        const exportResult = await exportBiodataToExcel(exportData, {
          filename,
          statusFilter: statusFilter || exportScope,
          includeDocuments,
          includePhotos,
        });

        setExportStatus({
          ...initialExportStatus,
          stage: "completed",
          totalRecords: exportData.length,
          filename: exportResult.filename,
          scope: exportScope,
        });

        if (notifications.showNotification) {
          showExportNotification(
            notifications.showNotification,
            "success",
            {
              totalRecords: exportData.length,
              filename: exportResult.filename,
              durationSeconds: (Date.now() - startTimestamp) / 1000,
              scope: exportScope,
            }
          );
        }

        return { success: true, ...exportResult };
      } catch (error) {
        console.error("Error exporting biodata:", error);

        setExportStatus((prev) => ({
          ...prev,
          isExporting: false,
          stage: "error",
          error: error.message || "Export failed",
        }));

        if (notifications.showNotification) {
          showExportNotification(notifications.showNotification, "error", {
            error: error.message,
            scope: exportScope,
          });
        }

        throw error;
      }
    },
    [
      apiEndpoints.export,
      biodata,
      filters,
      initialExportStatus,
      notifications.showNotification,
      totalItems,
    ]
  );

  const printBiodata = useCallback(
    (selectedIds = []) => {
      const ids =
        selectedIds.length > 0 ? selectedIds : biodata.map((b) => b.id);
      const printUrl = `${apiEndpoints.print}?ids=${ids.join(",")}`;
      window.open(printUrl, "_blank");
    },
    [apiEndpoints.print, biodata]
  );

  const resetExportStatus = useCallback(() => {
    setExportStatus(initialExportStatus);
  }, [initialExportStatus]);

  // Computed values
  const filteredCount = useMemo(() => {
    return totalItems;
  }, [totalItems]);

  const hasFilters = useMemo(() => {
    return Object.values(filters).some(
      (value) => value !== "" && value !== null && value !== undefined
    );
  }, [filters]);

  // Reset all state
  const resetAll = useCallback(() => {
    resetFilters();
    resetPagination();
    resetData();
    bulkActions.clearSelection();
    modals.closeAll();
    notifications.clearNotifications();
  }, [
    resetFilters,
    resetPagination,
    resetData,
    bulkActions,
    modals,
    notifications,
  ]);

  return {
    // Data
    biodata,
    loading,
    error,
    analytics,

    // Pagination
    page,
    totalPages,
    totalItems,
    itemsPerPage: currentPageSize,
    filteredCount,
    goToPage,
    changePageSize,

    // Filters
    filters,
    updateFilter,
    resetFilters,
    hasFilters,

    // Bulk actions
    ...bulkActions,
    handleBulkApprove,
    handleBulkReject,
    handleBulkGrantPermission,

    // Modals
    ...modals,

    // Notifications
    ...notifications,

    // Actions
    approveBiodata,
    rejectBiodata,
    grantEditPermission,
    exportBiodata,
    printBiodata,
    refetch,
    resetAll,
    exportStatus,
    resetExportStatus,

    // Computed
    isEmpty: biodata.length === 0 && !loading,
    hasData: biodata.length > 0,
    selectedEditData,
    setSelectedEditData,
  };
};
